import ExcelJS from "exceljs"

import type { ITableColumn, ITableData } from "../models/table"
import { CSV_DELIMITER, CSV_NEW_LINE } from "./constants"

const XLSX_CONTENT_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"

/**
 * Converts tabular data to a CSV
 *
 * @param data - The tabular data
 * @returns - The CSV
 * @example
 * convertTabularDataToCSV(tabularData)
 */
// Mass eslint disable @typescript-eslint/no-explicit-any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const convertTabularDataToCSV = (data: ITableData<any>): string => {
  if (!data.columns.length) {
    return ""
  }

  return (
    [
      // Enclose header titles in quotes to preserve commas
      data.columns.map((header) => `"${header.title}"`),
      ...data.rows.map((row) => {
        const columnKeys = data.columns.map((column) => column.key)

        return (
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          Object.entries(row)
            // Filter out the row data without a corresponding column
            .filter((entry) => {
              const key: string = entry[0]

              return columnKeys.includes(key)
            })
            // Sort the row data by column order
            .sort(([cellAKey], [cellBKey]) => {
              // Mass eslint disable @typescript-eslint/no-explicit-any
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const cellAColumn: ITableColumn<any> | undefined =
                data.columns.find((column) => column.key === cellAKey)
              // Mass eslint disable @typescript-eslint/no-explicit-any
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const cellBColumn: ITableColumn<any> | undefined =
                data.columns.find((column) => column.key === cellBKey)

              return (cellAColumn?.order ?? 0) - (cellBColumn?.order ?? 0)
            })
            // Transform the row data to CSV friendly values
            .map((entry) => {
              const value = entry[1]

              // Enclose string cell values in quotes to preserve commas
              if (typeof value === "string") {
                return `"${value}"`
              }
              if (value instanceof Date) {
                return value.toISOString()
              }
              if (Number.isNaN(value)) {
                return ""
              }
              return value ?? ""
            })
        )
      }),
    ]
      // Separate values with commas and turn into a string
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      .map((row) => row.join(CSV_DELIMITER))
      // Separate rows with new lines and turn into a string
      .join(CSV_NEW_LINE)
  )
}

/**
 * Creates an anchor used for downloading
 *
 * @param fileData - The file data
 * @param fileName - The file name
 * @example
 * createDownloadElement(new Blob([csv], { type: "text/csv" }), "My Report")
 */
const createDownloadElement = (
  fileData: Blob | MediaSource,
  fileName: string,
  extension = "csv"
): HTMLAnchorElement => {
  const anchor: HTMLAnchorElement = document.createElement("a")
  anchor.href = window.URL.createObjectURL(fileData)
  anchor.download = `${fileName}.${extension}`

  return anchor
}

const tabularCellValue = (value: unknown): boolean | number | string => {
  if (typeof value === "string") {
    return value
  }
  if (value instanceof Date) {
    return value.toISOString()
  }
  if (typeof value === "number") {
    return Number.isNaN(value) ? "" : value
  }
  if (typeof value === "boolean") {
    return value
  }

  return ""
}

const columnsInOrder = <TTableRow extends { id: number | string }>(
  columns: ITableColumn<TTableRow>[]
): ITableColumn<TTableRow>[] =>
  [...columns].sort((left, right) => left.order - right.order)

const writeXlsxBuffer = async (
  // Mass eslint disable @typescript-eslint/no-explicit-any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: ITableData<any>
) => {
  const workbook = new ExcelJS.Workbook()
  const sheet = workbook.addWorksheet("Sheet1")
  const columns = columnsInOrder(data.columns)

  if (columns.length > 0) {
    sheet.addRow(columns.map((column) => column.title))
    for (const row of data.rows) {
      sheet.addRow(
        columns.map((column) =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          tabularCellValue(row[column.key])
        )
      )
    }
  }

  return workbook.xlsx.writeBuffer()
}

/**
 * Clicks and removes an anchor used for downloading
 *
 * @param anchor - The anchor
 * @example
 * invokeDownloadAndCleanup(anchor)
 */
const invokeDownloadAndCleanup = (anchor: HTMLAnchorElement): void => {
  anchor.click()
  window.URL.revokeObjectURL(anchor.href)
  anchor.remove()
}

/**
 * Downloads a CSV
 *
 * @param data - The data
 * @param filename - The name of the file
 * @example
 * downloadCsvNew(data, "My Report")
 */
export const downloadCsvNew = (
  // Mass eslint disable @typescript-eslint/no-explicit-any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: ITableData<any>,
  filename: string
): void => {
  const csv: string = convertTabularDataToCSV(data)
  const downloadElement: HTMLAnchorElement = createDownloadElement(
    new Blob([csv], { type: "text/csv" }),
    filename
  )

  invokeDownloadAndCleanup(downloadElement)
}

/**
 * Downloads an Excel workbook of the same table `downloadCsvNew` would write.
 *
 * @param data - The data
 * @param filename - The name of the file without extension
 * @example
 * downloadXlsxNew(data, "My Report")
 */
export const downloadXlsxNew = (
  // Mass eslint disable @typescript-eslint/no-explicit-any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: ITableData<any>,
  filename: string
): void => {
  void writeXlsxBuffer(data)
    .then((buffer) => {
      const downloadElement: HTMLAnchorElement = createDownloadElement(
        new Blob([buffer as BlobPart], { type: XLSX_CONTENT_TYPE }),
        filename,
        "xlsx"
      )

      invokeDownloadAndCleanup(downloadElement)
    })
    .catch((error: unknown) => {
      console.error(error)
    })
}
